import {
  chainDynamicValue,
  DynamicValue,
  useComponentState,
} from "@faedev/dynamic-values";
import { composeClassNames, ifDefined } from "@faedev/editor-utils";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { useCookies } from "react-cookie";
import FormSelect from "src/components/FormSelect";
import ModalLogin from "src/components/ModalLogin";
import ModalRegister from "src/components/ModalRegister";
import ModalResetPassword from "src/components/ModalResetPassword";
import Paragraph from "src/components/Paragraph";
import Translate from "src/components/Translate";
import constants from "src/constants";
import getCookieByName from "src/functions/getCookieByName";
import getLink from "src/functions/getLink";
import gutter from "src/functions/gutter";
import isFormValid from "src/functions/isFormValid";
import onChangeToValue from "src/functions/onChangeToValue";
import optionsLocale from "src/functions/optionsLocale";
import validateAuthToken from "src/functions/validateAuthToken";
import { sAlert } from "src/styles/sAlert.module.scss";
import { sButton } from "src/styles/sButton.module.scss";
import { sFormControl } from "src/styles/sFormControl.module.scss";
import { sFormGroup } from "src/styles/sFormGroup.module.scss";
import { sFormLabel } from "src/styles/sFormLabel.module.scss";
import { sNote } from "src/styles/sNote.module.scss";
import { DataClientForm } from "src/types/DataClientForm";

type Props = {
  userData: DynamicValue<DataClientForm>;
  setFormSave?: (setFormSave: boolean) => void;
  isClient?: boolean;
};

function CartAddressForm({ userData, setFormSave, isClient }: Props) {
  const { locale: lang, push } = useRouter();
  var [resultState, setResultState] = useState(null);
  var [validated, setValidated] = useState(false);
  var [alertMessage, setAlertMessage] = useState(false);

  const userId = chainDynamicValue(userData, "userId").value;
  const name = chainDynamicValue(userData, "name").value;
  const surname = chainDynamicValue(userData, "surname").value;
  const email = chainDynamicValue(userData, "email").value;
  const phone = chainDynamicValue(userData, "phone").value;
  const street = chainDynamicValue(userData, "street").value;
  const city = chainDynamicValue(userData, "city").value;
  const zip = chainDynamicValue(userData, "zip").value;
  const country = chainDynamicValue(userData, "country").value;
  const isFilledName = chainDynamicValue(userData, "isFilledName").value;
  const isFilledSurname = chainDynamicValue(userData, "isFilledSurname").value;

  const [agreement, setAgreement] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordAgain, setPasswordAgain] = useState("");
  var [cookies, setCookie] = useCookies(null);

  var openModalLogin = useComponentState(false);
  var openModalRegister = useComponentState(false);
  var openModalResetPassword = useComponentState(false);

  const passwordValidation = password.length !== 0 && password.length < 8;

  return (
    <Form id={`ChangeAddress`} validated={validated} noValidate>
      <Form.Row className="justify-content-md-center">
        <Col lg={6} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Name`} />*
            </Form.Label>
            <Form.Control
              className={sFormControl}
              required={true}
              name={`name`}
              id={`name`}
              readOnly={isFilledName === true ? true : false}
              value={chainDynamicValue(userData, "name").value}
              onChange={function (param2) {
                return chainDynamicValue(userData, "name").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type="invalid">
              <Translate tKey={constants.Form.mandatoryMessage} />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Surname`} />*
            </Form.Label>
            <Form.Control
              className={sFormControl}
              required={true}
              name={`surname`}
              id={`surname`}
              readOnly={isFilledSurname === true ? true : false}
              value={chainDynamicValue(userData, "surname").value}
              onChange={function (param2) {
                return chainDynamicValue(userData, "surname").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type="invalid">
              <Translate tKey={constants.Form.mandatoryMessage} />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Form.Row>
      <Form.Row className="justify-content-md-center">
        <Col lg={6} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Email`} />*
            </Form.Label>
            <Form.Control
              className={sFormControl}
              required={true}
              name={`email`}
              id={`email`}
              readOnly={isClient ? true : false}
              value={chainDynamicValue(userData, "email").value}
              onChange={function (param2) {
                return chainDynamicValue(userData, "email").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type="invalid">
              <Translate tKey={constants.Form.mandatoryMessage} />
            </Form.Control.Feedback>
            <Form.Text className={sNote}>
              <Translate tKey={constants.Form.emailNote} />
            </Form.Text>
          </Form.Group>
        </Col>
        <Col lg={6} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Phone`} />
            </Form.Label>
            <Form.Control
              className={sFormControl}
              name={`phone`}
              id={`phone`}
              value={chainDynamicValue(userData, "phone").value}
              onChange={function (param2) {
                return chainDynamicValue(userData, "phone").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type="invalid">
              <Translate tKey={constants.Form.mandatoryMessage} />
            </Form.Control.Feedback>
            <Form.Text className={sNote}>
              <Translate tKey={constants.Form.phoneNote} />
            </Form.Text>
          </Form.Group>
        </Col>
      </Form.Row>
      {!isClient && (
        <Form.Row className="justify-content-md-center">
          <Col lg={6} xs={12}>
            <Form.Group>
              <Form.Label className={sFormLabel}>
                <Translate tKey={`Password`} />*
              </Form.Label>
              <Form.Control
                className={sFormControl}
                required={true}
                name={`password`}
                id={`password`}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                isInvalid={passwordValidation}
              />
              <Form.Control.Feedback type="invalid">
                {passwordValidation ? (
                  <Translate tKey={`Password is too short.`} />
                ) : (
                  <Translate tKey={constants.Form.mandatoryMessage} />
                )}
              </Form.Control.Feedback>
              <Form.Text className={sNote}>
                <Translate tKey={`The minimum length is 8 characters.`} />
              </Form.Text>
            </Form.Group>
          </Col>
          <Col lg={6} xs={12}>
            <Form.Group>
              <Form.Label className={sFormLabel}>
                <Translate tKey={`Password again`} />*
              </Form.Label>
              <Form.Control
                className={sFormControl}
                required={true}
                name={`passwordAgain`}
                id={`passwordAgain`}
                type="password"
                value={passwordAgain}
                onChange={(e) => setPasswordAgain(e.target.value)}
                isInvalid={password !== passwordAgain}
              />
              <Form.Control.Feedback type="invalid">
                {password !== passwordAgain ? (
                  <Translate tKey={`Passwords do not match.`} />
                ) : (
                  <Translate tKey={constants.Form.mandatoryMessage} />
                )}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Form.Row>
      )}
      <Form.Row className="justify-content-md-center">
        <Col lg={6} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Street`} />
            </Form.Label>
            <Form.Control
              className={sFormControl}
              name={`street`}
              id={`street`}
              value={chainDynamicValue(userData, "street").value}
              onChange={function (param2) {
                return chainDynamicValue(userData, "street").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type="invalid">
              <Translate tKey={constants.Form.mandatoryMessage} />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`City`} />
            </Form.Label>
            <Form.Control
              className={sFormControl}
              name={`city`}
              id={`city`}
              value={chainDynamicValue(userData, "city").value}
              onChange={function (param2) {
                return chainDynamicValue(userData, "city").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type="invalid">
              <Translate tKey={constants.Form.mandatoryMessage} />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Form.Row>
      <Form.Row className="justify-content-md-center">
        <Col lg={6} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Zip`} />
            </Form.Label>
            <Form.Control
              className={sFormControl}
              name={`zip`}
              id={`zip`}
              value={chainDynamicValue(userData, "zip").value}
              onChange={function (param2) {
                return chainDynamicValue(userData, "zip").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type="invalid">
              <Translate tKey={constants.Form.mandatoryMessage} />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Country`} />
            </Form.Label>
            <FormSelect
              as="select"
              options={optionsLocale(`country`, lang)}
              className={sFormControl}
              id={`country`}
              name={`country`}
              value={chainDynamicValue(userData, "country").value}
              onChange={function (param2) {
                return chainDynamicValue(userData, "country").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type="invalid">
              <Translate tKey={constants.Form.mandatoryMessage} />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Form.Row>
      {!isClient && (
        <Form.Row className="justify-content-md-center">
          <Col xs={12}>
            <Form.Group
              className={composeClassNames([
                sFormGroup,
                `form-check-agreement`,
              ])}
              style={gutter(`margin`, `vertical`, `md`)}
            >
              <div>
                <Form.Check.Input
                  className={sFormControl}
                  type={`checkbox`}
                  name={`agreement`}
                  id={`agreement`}
                  required={true}
                  onChange={() => setAgreement(!agreement)}
                  checked={agreement}
                />
                <Form.Check.Label className={sFormLabel} htmlFor={`conditions`}>
                  <Translate tKey={`I agree with`} />
                  {` `}
                  <Link
                    href={getLink(lang, `terms-and-privacy`, null)}
                    passHref
                    locale={false}
                  >
                    <Button
                      variant="link"
                      type="button"
                      as="a"
                      className={composeClassNames([sButton, "btn-inline"])}
                    >
                      <Translate
                        tKey={`business conditions and rules of personal data protection`}
                      />
                    </Button>
                  </Link>
                  {`.`}
                </Form.Check.Label>
              </div>
            </Form.Group>
          </Col>
        </Form.Row>
      )}
      <Row className="text-center mb-4 mt-4">
        <Col>
          {isClient ? (
            <Button
              variant="primary"
              type="button"
              className={composeClassNames([sButton, "btn-cta"])}
              onClick={function () {
                setValidated(true);
                if (isFormValid(`ChangeAddress`)) {
                  setResultState(true);
                  setAlertMessage(true);
                  fetch(`${constants.Paths.apiUrl}/clients/${userId}`, {
                    method: "PUT",
                    headers: validateAuthToken(getCookieByName(`auth-token`)),
                    body: JSON.stringify({
                      name,
                      surname,
                      email,
                      phone,
                      address: {
                        street,
                        city,
                        zip,
                        country: country ? country : "CZ",
                      },
                    }),
                  }).then(() => {
                    setFormSave(true);
                  });
                }
              }}
            >
              <Translate tKey={`Save changes`} />
            </Button>
          ) : (
            <Button
              variant="primary"
              type="button"
              className={composeClassNames([sButton, "btn-cta"])}
              onClick={function () {
                setValidated(true);
                if (
                  isFormValid(`ChangeAddress`) &&
                  password === passwordAgain &&
                  !passwordValidation
                ) {
                  setResultState(true);
                  setAlertMessage(true);
                  fetch(`${constants.Paths.apiUrl}/auth/local/register`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                      email,
                      username: email,
                      password,
                      agreement,
                    }),
                  }).then(async (res) => {
                    if (res.ok) {
                      const data = await res.json();
                      setCookie(
                        `auth-token`,
                        data.jwt,
                        constants.CookiesOptions
                      );
                      fetch(
                        `${constants.Paths.apiUrl}/clients/${data.user.id}`,
                        {
                          method: "PUT",
                          headers: validateAuthToken(
                            getCookieByName(`auth-token`)
                          ),
                          body: JSON.stringify({
                            name,
                            surname,
                            email,
                            phone,
                            address: {
                              street,
                              city,
                              zip,
                              country: country ? country : "CZ",
                            },
                          }),
                        }
                      ).then(() => {
                        setFormSave(true);
                        push(`${constants.Paths.registeredParam}`);
                      });
                    } else {
                      let status = await res.json();

                      if (
                        status.message[0].messages[0].message ===
                        "Email is already taken."
                      ) {
                        openModalLogin.change(true);
                      }
                    }
                  });
                }
              }}
              size="lg"
            >
              <Translate tKey={`Continue`} />
            </Button>
          )}
          {validated && !isFormValid(`ChangeAddress`) ? (
            <Alert
              variant="danger"
              className={sAlert}
              style={gutter(`margin`, `vertical`, `md`)}
            >
              <Translate tKey={constants.Form.errorMessage} />
            </Alert>
          ) : null}
          {validated && password !== passwordAgain ? (
            <Alert
              variant="danger"
              className={sAlert}
              style={gutter(`margin`, `vertical`, `md`)}
            >
              <Translate tKey={`Passwords do not match.`} />
            </Alert>
          ) : null}
          {ifDefined(
            resultState,
            (value) =>
              value
                ? alertMessage && (
                    <Alert
                      variant="success"
                      className={sAlert}
                      style={gutter(`margin`, `vertical`, `md`)}
                    >
                      <Translate tKey={constants.Form.successMessage} />
                    </Alert>
                  )
                : alertMessage && (
                    <Alert
                      variant="danger"
                      className={sAlert}
                      style={gutter(`margin`, `vertical`, `md`)}
                    >
                      <Translate tKey={constants.Form.errorUnknownMessage} />
                    </Alert>
                  ),
            () => null
          )}
        </Col>
      </Row>
      <ModalLogin
        openModalLogin={openModalLogin}
        openModalRegister={openModalRegister}
        openModalResetPassword={openModalResetPassword}
        key={`ModalLogin`}
        alert={
          <Alert variant={`warning`} className={sAlert}>
            <Paragraph
              width={`semibold`}
              align="center"
              style={gutter(`margin`, `all`, `0`)}
            >
              <Translate tKey={`Email is already registered. Please login.`} />
            </Paragraph>
          </Alert>
        }
      />
      <ModalRegister
        openModalLogin={openModalLogin}
        openModalRegister={openModalRegister}
        key={`ModalRegister`}
      />
      <ModalResetPassword
        openModalResetPassword={openModalResetPassword}
        key={`ModalResetPassword`}
        // setAlertResetPassword={setAlertResetPassword}
      />
    </Form>
  );
}

export default CartAddressForm;
